import { z } from 'zod'

const apiDateInputSchema = z.union([z.date(), z.string().min(1)])

function parseApiDateInput(
  value: z.infer<typeof apiDateInputSchema>,
  ctx: z.RefinementCtx,
): Date {
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) {
    ctx.addIssue({ code: z.ZodIssueCode.invalid_date })
    return z.NEVER
  }

  return date
}

/** API string fields that arrive as `null` / missing → normalized to `null`. */
export const nullableStringSchema = z
  .string()
  .nullish()
  .transform((value) => value ?? null)

/** Numeric fields that arrive as `null` / missing → normalized to `null`. */
export const nullableNumberSchema = z
  .number()
  .nullish()
  .transform((value) => value ?? null)

/** Positive numbers only; `null` / missing → `null`. */
export const nullablePositiveNumberSchema = z
  .number()
  .min(0)
  .nullish()
  .transform((value) => value ?? null)

/** Date string or `Date`; `null` / missing → `null`. */
export const nullableDateSchema = z
  .union([z.date(), z.string().min(1)])
  .nullish()
  .transform((value, ctx) => {
    if (value == null) {
      return null
    }

    return parseApiDateInput(value, ctx)
  })

/** Required date string or `Date` → `Date`. */
export const requiredDateSchema = apiDateInputSchema.transform((value, ctx) => {
  return parseApiDateInput(value, ctx)
})

export const yearWeekSchema = z
  .string()
  .regex(
    /^\d{4}(0[1-9]|[1-4]\d|5[0-3])$/,
    'Expected format "YYYYww" (e.g. "202614")',
  )
  .transform((val) => {
    const num = Number(val)
    if (!Number.isFinite(num)) {
      throw new Error(`Failed to convert "${val}" to a finite number`)
    }
    return num
  })
